import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShieldCheck, CreditCard, Clock, Lock, Zap } from 'lucide-react';
type PlanId = 'basic' | 'pro';
type Plan = {
  id: PlanId;
  emoji: string;
  name: string;
  description: string;
  oldPrice: string;
  price: string;
  savings?: string;
  badge?: string;
  highlighted: boolean;
  ctaLabel: string;
  href: string;
};
const plans: Plan[] = [{
  id: 'basic',
  emoji: '📘',
  name: 'CROSSFIT RESUELTO',
  description: '6 tomos + 6 bonos',
  oldPrice: '$34.990',
  price: '$19.990',
  highlighted: false,
  ctaLabel: 'SÍ, QUIERO CROSSFIT RESUELTO',
  href: 'https://pagos.recursosfitness.shop/cart/44326014025825:1'
}, {
  id: 'pro',
  emoji: '🏆',
  name: 'PACK COMPLETO',
  description: '6 tomos + 6 bonos + Crossfit Pro™',
  oldPrice: '$43.990',
  price: '$27.990',
  savings: 'AHORRÁS $16.000',
  badge: 'MÁS ELEGIDO',
  highlighted: true,
  ctaLabel: 'SÍ, QUIERO PACK COMPLETO',
  href: 'https://pagos.recursosfitness.shop/cart/44326140641377:1'
}];
export function Pricing() {
  const [selected, setSelected] = useState<PlanId>('pro');
  const activePlan = plans.find(p => p.id === selected) ?? plans[1];
  return <section id="pricing" className="py-12 md:py-24 px-4 bg-cf-darker" data-id="element-281">
      <div className="max-w-2xl mx-auto" data-id="element-282">
        <div className="text-center mb-8 md:mb-12" data-id="element-283">
          <h2 className="font-bebas text-4xl md:text-7xl text-white tracking-tight mb-2 md:mb-4" data-id="element-284">
            PRECIOS Y OFERTAS
          </h2>
          <p className="text-sm md:text-xl text-gray-300 font-medium" data-id="element-285">
            Las dos opciones incluyen los 6 bonos estratégicos completos
          </p>
        </div>

        {/* Radio cards */}
        <div className="space-y-3 md:space-y-4" role="radiogroup" aria-label="Elegí tu opción" data-id="element-286">
          {plans.map(plan => {
          const isSelected = selected === plan.id;
          const isPro = plan.id === 'pro';
          return <button key={plan.id} type="button" role="radio" aria-checked={isSelected} onClick={() => setSelected(plan.id)} className={`w-full text-left rounded-2xl p-4 md:p-6 transition-all relative ${isSelected ? isPro ? 'bg-cf-card border-2 border-cf-gold shadow-[0_0_30px_rgba(245,184,0,0.2)]' : 'bg-cf-card border-2 border-white' : 'bg-cf-card border-2 border-white/10 hover:border-white/25'}`} data-id="element-287">
                {plan.badge && isSelected && <div className="absolute -top-2.5 left-4 md:left-6 bg-cf-gold text-black font-bold px-2.5 py-0.5 rounded-full text-[10px] md:text-xs tracking-wider uppercase whitespace-nowrap" data-id="element-288">
                    ⭐ {plan.badge}
                  </div>}
                <div className="flex items-center gap-3 md:gap-4" data-id="element-289">
                  {/* Radio */}
                  <div className={`shrink-0 w-5 h-5 md:w-6 md:h-6 rounded-full border-2 flex items-center justify-center transition-colors ${isSelected ? isPro ? 'border-cf-gold' : 'border-white' : 'border-white/30'}`} data-id="element-290">
                    <AnimatePresence data-id="element-291">
                      {isSelected && <motion.div initial={{
                    scale: 0
                  }} animate={{
                    scale: 1
                  }} exit={{
                    scale: 0
                  }} transition={{
                    duration: 0.15
                  }} className={`w-2.5 h-2.5 md:w-3 md:h-3 rounded-full ${isPro ? 'bg-cf-gold' : 'bg-white'}`} data-id="element-292" />}
                    </AnimatePresence>
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0 flex items-center justify-between gap-3" data-id="element-293">
                    <div className="min-w-0" data-id="element-294">
                      <div className="flex items-center gap-1.5 md:gap-2 mb-0.5" data-id="element-295">
                        <span className="text-base md:text-xl" data-id="element-296">
                          {plan.emoji}
                        </span>
                        <h3 className="font-bebas text-lg md:text-2xl text-white tracking-wide truncate" data-id="element-297">
                          {plan.name}
                        </h3>
                      </div>
                      {isSelected && plan.savings ? <div className="flex flex-wrap items-center gap-1.5 mb-1" data-id="element-298">
                          <span className="bg-cf-gold/15 text-cf-gold border border-cf-gold/40 px-1.5 py-0.5 rounded text-[9px] md:text-[10px] font-bold tracking-wider" data-id="element-299">
                            {plan.savings}
                          </span>
                        </div> : null}
                      <p className="text-gray-400 text-[11px] md:text-sm leading-snug" data-id="element-300">
                        {plan.description}
                      </p>
                    </div>
                    <div className="text-right shrink-0" data-id="element-301">
                      <div className="text-[11px] md:text-sm text-gray-500 line-through leading-none mb-0.5" data-id="element-302">
                        {plan.oldPrice}
                      </div>
                      <div className={`font-bebas text-2xl md:text-4xl leading-none ${isPro ? 'text-cf-gold' : 'text-white'}`} data-id="element-303">
                        {plan.price}
                      </div>
                    </div>
                  </div>
                </div>
              </button>;
        })}
        </div>

        {/* Shared CTA */}
        <motion.a key={activePlan.id} href={activePlan.href} initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.25
      }} className="mt-5 md:mt-6 w-full bg-cf-gold hover:bg-cf-goldHover text-black font-bebas text-xl md:text-3xl py-4 md:py-5 px-4 rounded-xl transition-colors uppercase tracking-wide flex items-center justify-center gap-2 leading-tight shadow-[0_8px_30px_rgba(245,184,0,0.25)]" data-id="element-304">
          {activePlan.ctaLabel} →
        </motion.a>

        <p className="text-center text-xs md:text-sm text-gray-400 font-medium mt-3 md:mt-4 italic" data-id="element-305">
          {activePlan.id === 'pro' ? 'La opción más elegida por instructores que entrenan todos los niveles' : 'Tu año de programación resuelto, listo para usar'}
        </p>

        {/* Trust badges */}
        <div className="mt-5 md:mt-6 bg-cf-card border border-white/10 rounded-xl px-4 py-3 md:py-4" data-id="element-306">
          <div className="flex flex-wrap justify-center items-center gap-x-4 gap-y-2 md:gap-x-6 text-[11px] md:text-sm text-gray-300 font-medium" data-id="element-307">
            <span className="flex items-center gap-1.5" data-id="element-308">
              <Lock className="w-4 h-4 text-cf-gold" data-id="element-309" /> Pago seguro
            </span>
            <span className="flex items-center gap-1.5" data-id="element-310">
              <Zap className="w-4 h-4 text-cf-gold" data-id="element-311" /> Acceso en 5 min
            </span>
            <span className="flex items-center gap-1.5" data-id="element-312">
              <ShieldCheck className="w-4 h-4 text-cf-gold" data-id="element-313" /> Garantía 7 días
            </span>
            <span className="flex items-center gap-1.5" data-id="element-314">
              <CreditCard className="w-4 h-4 text-cf-gold" data-id="element-315" /> Mercado Pago
            </span>
          </div>
        </div>
      </div>
    </section>;
}